/**
 * Pack 12.A — Design System: <LoadingState />
 *
 * Canonical "we're fetching it" surface. Pair with <Skeleton /> when you
 * have stable layout you can ghost-render; use this when you need a
 * centered spinner + label.
 */
import * as React from "react";

import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

export interface LoadingStateProps {
  /** Optional label rendered beside the spinner. Defaults to "Loading…". */
  label?: React.ReactNode;
  /** Visual size of the spinner (matches Tailwind size-* utility values). */
  size?: "sm" | "md" | "lg";
  /** Container class. */
  className?: string;
  /** Hide the visible label but keep aria-busy + sr-only text. */
  hideLabel?: boolean;
}

const SIZE_CLASS: Record<NonNullable<LoadingStateProps["size"]>, string> = {
  sm: "size-4",
  md: "size-6",
  lg: "size-8",
};

export function LoadingState({
  label = "Loading…",
  size = "md",
  className,
  hideLabel = false,
}: LoadingStateProps) {
  return (
    <div
      role="status"
      aria-busy
      aria-live="polite"
      className={cn(
        "flex flex-col items-center justify-center gap-3 p-6 text-muted-foreground",
        className,
      )}
    >
      <Spinner className={SIZE_CLASS[size]} />
      <span className={cn("text-sm", hideLabel && "sr-only")}>{label}</span>
    </div>
  );
}
